/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { observer } from 'mobx-react';
import { useViewModel } from '../../../hooks/useViewModel';
import { FormattedMessage } from 'react-intl';
import { Box, Button, IconButton, Link, TextField, Typography } from '@material-ui/core';
import React from 'react';
import CancelIcon from '@material-ui/icons/Cancel';
import { ResetPasswordFormViewModel } from './ResetPasswordFormViewModel';
import ResetPasswordFormUseStyles from './ResetPasswordFormStyles';
import { useLocation } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';

const ResetPasswordFormView = observer(() => {
	const classes = ResetPasswordFormUseStyles();
	const viewModel: ResetPasswordFormViewModel = useViewModel(ResetPasswordFormViewModel);
	const search = useLocation().search;
	const code = new URLSearchParams(search).get('code');

	return viewModel.isLoading() ? (
		<Box className={classes.circularProgress}>
			<CircularProgress />
		</Box>
	) : (
		<>
			<Typography className={classes.separator} variant={'h4'}>
				<FormattedMessage id='resetPassword.new_password' />
			</Typography>
			<form
				onSubmit={async (e) => {
					e.preventDefault();
					await viewModel.updatePassword(code, viewModel.getPasswordOne(), viewModel.getPasswordTwo());
				}}
				onError={(errors) => console.log(errors)}
				className={classes.formRoot}>
				<label id='password1' className={classes.label}>
					<FormattedMessage id={'resetPassword.enter_password'} />
				</label>
				<TextField
					required
					type='password'
					name='password1'
					variant='outlined'
					fullWidth={true}
					size={'small'}
					value={viewModel.getPasswordOne()}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => viewModel.setPasswordOne(e)}
				/>
				<label id='password2' className={classes.label}>
					<FormattedMessage id={'resetPassword.confirm_password'} />
				</label>
				<TextField
					required
					type='password'
					name='password2'
					variant='outlined'
					fullWidth={true}
					size={'small'}
					value={viewModel.getPasswordTwo()}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => viewModel.setPasswordTwo(e)}
				/>

				<Box display={'flex'} justifyContent={'flex-end'}>
					<Button
						disabled={viewModel.disableChangeButton()}
						variant='contained'
						color='secondary'
						type='submit'
						className={classes.submitButton}>
						<FormattedMessage id='resetPassword.change_password' />
					</Button>
				</Box>
			</form>
			<div className={classes.cancelContainer} onClick={() => viewModel.cancel()}>
				<IconButton className={classes.cancelButton}>
					<CancelIcon />
				</IconButton>
				<Link className={classes.cancelText}>
					<FormattedMessage id={'resetPassword.cancel'} />
				</Link>
			</div>
		</>
	);
});

export default ResetPasswordFormView;
